import { IHttpService, IPromise, IRequestShortcutConfig } from "angular";
import { EnvironmentService } from "@gtmhub/env";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { ICollection } from "@gtmhub/models";
import { IRole } from "@gtmhub/roles";
import { IPermissionAction } from "@gtmhub/roles/models";
import { IAccess, IUserItemAllowedActions } from "@gtmhub/sessions";

type ItemType = "session" | "dashboard" | "kpi";

export class PermissionsService {
  public static $inject = ["$http", "EnvironmentService", "FeatureToggleService"];

  constructor(private $http: IHttpService, private env: EnvironmentService, private featureToggleService: FeatureToggleService) {}

  public getEffectivePermissions(): IPromise<string[]> {
    const url = this.env.getApiEndpoint("/permissions/effective");

    return this.$http.get<string[]>(url).then((response) => response.data);
  }

  public getPermissionsByPrincipal(id: string): IPromise<string[]> {
    const url = this.env.getApiEndpoint("/permissions/principal/" + id);

    return this.$http.get<string[]>(url).then((response) => response.data);
  }

  public getPermissionActions(): IPromise<IPermissionAction[]> {
    const url = this.env.getApiEndpoint("/permissions/actions");

    return this.$http.get<ICollection<IPermissionAction>>(url).then((response) => {
      const permissions = response.data.items;
      return this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureReflections).then((isReflectionsFeatureEnabled) => {
        if (!isReflectionsFeatureEnabled) {
          // remove the reflections permission, as this is not to be shown
          // on the client web app until the feature is released
          //task to remove the toggle when Reflections feature is released https://gtmhub.atlassian.net/browse/GVS-18111
          return permissions.filter((permission) => permission.name !== "ManageReflections");
        }
        return permissions;
      });
    });
  }

  public updatePrincipalPermissions(id: string, allowedActions: string[]): IPromise<void> {
    const url = this.env.getApiEndpoint("/permissions/principal/" + id);

    return this.$http.put(url, allowedActions).then(() => null);
  }

  public getCurrentUserAllowedActionsPerItem(targetId: string, targetType: string): IPromise<IUserItemAllowedActions> {
    const url: string = this.env.getApiEndpoint(`/permissions/currentUser-allowedActions/${targetId}`);
    const query: IRequestShortcutConfig = {
      params: {
        targetType: targetType,
      },
    };

    return this.$http.get<IUserItemAllowedActions>(url, query).then((response) => response.data);
  }

  public getDefaultAccess(itemType: ItemType): IPromise<IAccess> {
    const url = this.env.getApiEndpoint(`/permissions/default-access/${itemType}`);

    return this.$http.get<IAccess>(url).then((response) => response.data);
  }

  public getDefaultAccessById(itemType: ItemType, itemId: string): IPromise<IAccess> {
    const url = this.env.getApiEndpoint(`/permissions/default-access/${itemType}/${itemId}`);

    return this.$http.get<IAccess>(url).then((response) => response.data);
  }

  public getDefaultAccessForReadOnlyAccount = (roles: ICollection<IRole>): IAccess => {
    const adminRoleId = roles.items.find((role) => role.name === "admin").id;
    const userRoleId = roles.items.find((role) => role.name === "user").id;

    const access: IAccess = {
      inherits: false,
      permissions: [
        {
          principalKind: "account",
          principalId: JSON.parse(localStorage.getItem("accountId")),
          grant: {
            general: ["read"],
          },
        },
      ],
    };

    if (userRoleId) {
      access.permissions.push({
        principalKind: "role",
        principalId: userRoleId,
        grant: {
          general: ["read"],
        },
      });
    }

    if (adminRoleId) {
      access.permissions.push({
        principalKind: "role",
        principalId: adminRoleId,
        grant: {
          general: ["read", "create", "update", "delete", "modifyPermissions"],
        },
      });
    }

    return access;
  };
}
